import React, { ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { Collapse } from 'components';

import { RuleBody } from '../rule/components/rule-body';
import { RuleFooter } from '../rule/components/rule-footer';
import { getEmptyCondition, toggleAttrOptions, findAttribute, Attribute } from '../rule/rule-utils';
import { RateCardTermRate } from './components/rate-card-term-rate';
import { RateCardTermHeader } from './components/rate-card-term-header';
import { useAttrOptionsState } from '../hooks';
import type { Condition } from '../rule/rule-utils';

import type {
	ClientRateCardTerm,
	RateCartTermUpdater,
	RateCardRateBaseOption,
	RateCardTermTranslations,
	TermGuid
} from './types';

import './rate-card-term.scss';

export interface RateCardTermProps {
	term: ClientRateCardTerm;
	contextSelector: string;
	// Assumed all terms within one rate card has the same attribute list
	attributes: Attribute[];
	translations: RateCardTermTranslations;
	rateBaseOptions: RateCardRateBaseOption[];
	updateTermById(id: TermGuid, updater: Partial<RateCartTermUpdater>): void;
	maxAttributePathItems?: number;
	conditionDivider?: ReactNode;
	popupContainerClass?: string;
	isPanelOpen?: boolean;
	readOnly?: boolean;
	removeTermById?(id: TermGuid): void;
	copyTermById?(id: TermGuid): void;
	togglePanelOpenById?(id: TermGuid): void;
}

// TODO: items to discuss
//  -description - either remove from design or add field to the RateTerm model
const RateCardTerm = ({
	term,
	translations,
	conditionDivider,
	contextSelector,
	attributes,
	rateBaseOptions,
	popupContainerClass,
	updateTermById,
	removeTermById,
	copyTermById,
	isPanelOpen,
	togglePanelOpenById,
	maxAttributePathItems = 1,
	readOnly
}: RateCardTermProps) => {
	const [attrOptions, setAttrOptions] = useAttrOptionsState(term.conditions, attributes);
	const isTermDisabled = readOnly || !term.active;

	const removeTerm = removeTermById
		? (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
				e.stopPropagation();
				removeTermById(term.guid);
		  }
		: undefined;

	const copyTerm = copyTermById
		? (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
				e.stopPropagation();
				copyTermById(term.guid);
		  }
		: undefined;

	const removeCondition = (condition: Condition) => {
		updateTermById(term.guid, {
			conditions: term.conditions.filter(cond => cond.id !== condition.id)
		});
		const attribute = findAttribute(attrOptions, condition.path);
		if (attribute) {
			setAttrOptions(toggleAttrOptions(attr => attr.path === attribute.path, { disabled: false }, attrOptions));
		}
	};

	const handleChange = (condition, value) => {
		const mappedConditions = term.conditions.map(termCondition =>
			termCondition.id === condition.id
				? {
						...condition,
						value
				  }
				: termCondition
		);
		updateTermById(term.guid, {
			conditions: mappedConditions
		});
	};

	const addCondition = (key: string) => {
		const attribute = findAttribute(attrOptions, key);
		if (!attribute) return;

		const { type, options, path, display_path } = attribute;
		if (!type || !display_path) return;

		const newCondition = getEmptyCondition({
			name: display_path,
			index: term.conditions.length,
			path,
			type,
			options,
			onChange: handleChange,
			onRemove: removeCondition
		});
		updateTermById(term.guid, {
			conditions: [...term.conditions, newCondition]
		});
	};

	const updateTerm = (updater: Partial<RateCartTermUpdater>) => updateTermById(term.guid, updater);
	const getPopupContainer = (target: HTMLElement) => {
		if (!popupContainerClass || !target) {
			return document.body;
		}
		return (target.closest(`.${popupContainerClass}`) as HTMLElement) || document.body;
	};

	const togglePanelOpen = useMemo(
		() => (typeof togglePanelOpenById === 'function' ? () => togglePanelOpenById(term.guid) : undefined),
		[togglePanelOpenById, term.guid]
	);

	const termContextSelector = `${contextSelector}-${term.guid}`;
	const header = (
		<RateCardTermHeader
			termGuid={term.guid}
			titleText={term.term_title}
			isEnabled={term.active}
			copyTooltipText={translations.copyTooltipText}
			removeTooltipText={translations.removeTooltipText}
			inactiveText={translations.inactiveTerm}
			switchTooltipText={term.active ? translations.disableTerm : translations.enableTerm}
			updateTerm={updateTerm}
			onRemove={removeTerm}
			onCopy={copyTerm}
			readOnly={readOnly}
		/>
	);

	return (
		<Collapse
			className="rate-card-term-wrapper"
			defaultActiveKey={isPanelOpen ? 'bringg-collapse-panel' : ''}
			onChange={togglePanelOpen}
		>
			<Collapse.Panel header={header} key={'bringg-collapse-panel'}>
				<div
					data-test-id={`rate-card-term-${term.guid}`}
					className={classNames('rate-card-term', { 'disabled-term': isTermDisabled })}
				>
					<div className="rule-container rate-card-term-conditions">
						<RuleBody
							disabled={isTermDisabled}
							maxAttributePathItems={maxAttributePathItems}
							conditions={term.conditions}
							translations={translations}
							divider={conditionDivider}
							contextSelector={termContextSelector}
							handleChange={handleChange}
							removeCondition={removeCondition}
							parentContainerClass={popupContainerClass}
						/>

						<RuleFooter
							disabled={isTermDisabled}
							attributes={attrOptions}
							translations={translations}
							addCondition={addCondition}
							contextSelector={termContextSelector}
						/>
					</div>
					<div className="rate-card-term-rate">
						<RateCardTermRate
							disabled={isTermDisabled}
							titleText={translations.rateTitle}
							rateBase={term.rate_base}
							rateValue={term.rate_amount}
							rateCurrency={term.rate_currency}
							rateBaseOptions={rateBaseOptions}
							updateTerm={updateTerm}
							getPopupContainer={getPopupContainer}
						/>
					</div>
				</div>
			</Collapse.Panel>
		</Collapse>
	);
};

export default RateCardTerm;
